import React, { useState, useEffect } from "react";
import axios from "axios";

const RotationReport = ({ partida, set, teamNames, refreshKey }) => {
  const [jogadas, setJogadas] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("home"); // "home" ou "away"
  const [rotationData, setRotationData] = useState({ home: {}, away: {} });

  // Estados para filtros
  const [selectedSet, setSelectedSet] = useState(set);
  const [availableSets, setAvailableSets] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("all"); // all" ou número do atleta
  const [zoneType, setZoneType] = useState("start"); // startou "end"
  const [selectedFundamento, setSelectedFundamento] = useState("A"); // A, S, D
  const [selectedQuality, setSelectedQuality] = useState("#"); // #, =, -, /, +, !, all
  const [availableAthletes, setAvailableAthletes] = useState([]);

  // Buscar jogadas do set
  const fetchJogadas = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/matches/set/last20`,
        {
          set: selectedSet === "all" ? 1 : Number(selectedSet),
          partida,
          lines: 10000,
        }
      );
      setJogadas(response?.data[0]?.last20Scouts || []);
    } catch (error) {
      console.error("Erro ao buscar jogadas:", error);
    }
  };

  // Buscar sets disponíveis
  const fetchAvailableSets = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/matches/parametros`,
        { partida }
      );
      const ultimoSet = response?.data[0]?.ultimoSet || 1;
      const sets = Array.from({ length: ultimoSet }, (_, i) => i + 1);
      setAvailableSets(["all", ...sets]);
    } catch (error) {
      console.error("Erro ao buscar sets:", error);
    }
  };

  // Buscar atletas disponíveis
  const fetchAvailableAthletes = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/rotation`,
        { partida }
      );
      const teamData = response?.data[0];
      if (teamData && teamData[selectedTeam]) {
        const athletes = teamData[selectedTeam].map((athlete) => ({
          number: athlete.number,
          name: athlete.name,
        }));
        setAvailableAthletes([{ number: "all", name: "Todos" }, ...athletes]);
      }
    } catch (error) {
      console.error("Erro ao buscar atletas:", error);
    }
  };

  useEffect(() => {
    fetchAvailableSets();
  }, [partida]);

  useEffect(() => {
    fetchAvailableAthletes();
  }, [partida, selectedTeam]);

  useEffect(() => {
    fetchJogadas();
  }, [selectedSet, partida, refreshKey]);

  // Processar dados das rotações
  useEffect(() => {
    if (!jogadas.length) return;

    const homeRotations = {};
    const awayRotations = {};

    // Filtrar jogadas baseado nos critérios selecionados
    const filteredPlays = jogadas.filter((j) => {
      if (!j.cod || typeof j.cod !== "string" || j.cod.length < 6) return false;

      const team = j.cod[0] === "*" ? "home" : j.cod[0] === "a" ? "away" : null;
      if (team !== selectedTeam) return false;

      // Filtrar por fundamento
      if (j.cod[3] !== selectedFundamento) return false;

      // Filtrar por qualidade
      if (selectedQuality !== "all" && j.cod[5] !== selectedQuality)
        return false;

      // Filtrar por atleta
      if (selectedAthlete !== "all") {
        const athleteNumber = j.cod.slice(1, 3);
        if (athleteNumber !== selectedAthlete.toString().padStart(2, 0))
          return false;
      }

      // Filtrar por set (se não forall)
      // Assumindo que as jogadas já são do set correto devido ao fetch
      // Se necessário, adicionar lógica adicional aqui
      return true;
    });

    // Agrupar por rotação do levantador
    filteredPlays.forEach((j) => {
      const setterRotation =
        selectedTeam === "home" ? j.sett?.[0] : j.sett?.[1];
      const zone = zoneType === "start" ? j.sz : j.ez;

      if (!setterRotation || !zone) return;

      const rotations = selectedTeam === "home" ? homeRotations : awayRotations;

      if (!rotations[setterRotation]) {
        rotations[setterRotation] = {
          zones: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 },
        };
      }

      if (rotations[setterRotation].zones[zone] !== undefined) {
        rotations[setterRotation].zones[zone]++;
      }
    });

    setRotationData({ home: homeRotations, away: awayRotations });
  }, [
    jogadas,
    selectedTeam,
    selectedSet,
    selectedAthlete,
    zoneType,
    selectedFundamento,
    selectedQuality,
  ]);

  // Mapeamento de zona para posição na tabela
  const zoneToPosition = {
    4: { row: 0, col: 0 }, // linha 1, coluna 1 = zona 4
    3: { row: 0, col: 1 }, // linha 1, coluna 2 = zona 3
    2: { row: 0, col: 2 }, // linha 1, coluna 3 = zona 2
    7: { row: 1, col: 0 }, // linha 2, coluna 1 = zona 7
    8: { row: 1, col: 1 }, // linha 2, coluna 2 = zona 8
    9: { row: 1, col: 2 }, // linha 2, coluna 3 = zona 9
    5: { row: 2, col: 0 }, // linha 3, coluna 1 = zona 5
    6: { row: 2, col: 1 }, // linha 3, coluna 2 = zona 6
    1: { row: 2, col: 2 }, // linha 3, coluna 3 = zona 1
  };

  // Criar matriz 3x3 para cada rotação
  const createRotationMatrix = (rotationNumber) => {
    const matrix = Array(3)
      .fill()
      .map(() => Array(3).fill(0));
    const rotation = rotationData[selectedTeam][rotationNumber];

    if (rotation) {
      Object.entries(rotation.zones).forEach(([zone, count]) => {
        const pos = zoneToPosition[parseInt(zone)];
        if (pos) {
          matrix[pos.row][pos.col] = count;
        }
      });
    }

    return matrix;
  };

  // Renderizar uma quadra individual
  const renderCourt = (rotationNumber) => {
    const matrix = createRotationMatrix(rotationNumber);
    const flat = matrix.flat();
    const totalPlays = flat.reduce((sum, cell) => sum + cell, 0);
    // Descobrir o maior valor da matriz para pintar de vermelho
    const maxValue = Math.max(...flat);

    // Função para cor de fundo baseada no percentual
    function getCellColor(value, isMax) {
      if (totalPlays === 0 || value === 0) return "#fff";
      if (isMax) return "#e56363"; // vermelho para o maior
      // Gradiente de verde: 0% = #e5f9e7, 100% = #065f46
      const percent = value / totalPlays;
      // Interpolar entre verde claro e escuro
      const light = [229, 249, 231]; // #e5f9e7
      const dark = [6, 95, 70]; // #065f46
      const r = Math.round(light[0] + (dark[0] - light[0]) * percent);
      const g = Math.round(light[1] + (dark[1] - light[1]) * percent);
      const b = Math.round(light[2] + (dark[2] - light[2]) * percent);
      return `rgb(${r},${g},${b})`;
    }

    return (
      <div key={rotationNumber} className="flex flex-col items-center">
        <div className="text-sm font-semibold mb-2 text-gray-700">
          S{rotationNumber} ({totalPlays})
        </div>
        <div
          className="w-[200px] h-[200px] border-2 border-gray-800"
          style={{ borderTopWidth: "6px" }}
        >
          <table className="w-full h-full border-collapse">
            <tbody>
              {matrix.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, colIndex) => {
                    const isFirstRow = rowIndex === 0;
                    const isMax = cell === maxValue && cell > 0;
                    const percent =
                      totalPlays > 0
                        ? Math.round((cell / totalPlays) * 100)
                        : 0;
                    return (
                      <td
                        key={colIndex}
                        className="text-center font-bold border border-dotted border-gray-400"
                        style={{
                          width: "33.33%",
                          height: "33.33%",
                          borderWidth: "1px",
                          borderStyle: "dotted",
                          borderColor: "#9CA3AF",
                          borderBottom: isFirstRow
                            ? "2px solid #1F2937"
                            : undefined,
                          background: getCellColor(cell, isMax),
                          padding: 0,
                        }}
                      >
                        {cell > 0 ? (
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <span
                              className="text-xl font-extrabold leading-none"
                              style={{ lineHeight: 1 }}
                            >
                              {percent}%
                            </span>
                            <span
                              className="text-xs font-normal text-gray-700 opacity-80"
                              style={{ fontSize: "1rem" }}
                            >
                              {cell}
                            </span>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Mapeamento de fundamentos
  const fundamentosMap = {
    A: "Ataque",
    S: "Saque",
    D: "Defesa",
  };

  // Mapeamento de qualidades
  const qualidadesMap = {
    "#": "Ponto/Perfeito",
    "=": "Erro",
    "-": "Ruim",
    "/": "Muito Ruim",
    "+": "Muito Bom",
    "!": "Bom",
    all: "Todos",
  };

  return (
    <div className="mt-4">
      {/* Filtros */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Filtros do Relatório
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Seletor de time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time
            </label>
            <div className="flex  rounded-lg p-1">
              <button
                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
                  selectedTeam === "home"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setSelectedTeam("home")}
              >
                {teamNames?.home?.name || "Casa"}
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
                  selectedTeam === "away"
                    ? "bg-red-600 text-white"
                    : "text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setSelectedTeam("away")}
              >
                {teamNames?.away?.name || "Visitante"}
              </button>
            </div>
          </div>

          {/* Seletor de set */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Set
            </label>
            <select
              value={selectedSet}
              onChange={(e) => setSelectedSet(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableSets.map((set) => (
                <option key={set} value={set}>
                  {set === "all" ? "Todos os Sets" : `Set ${set}`}
                </option>
              ))}
            </select>
          </div>

          {/* Seletor de atleta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Atleta
            </label>
            <select
              value={selectedAthlete}
              onChange={(e) => setSelectedAthlete(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableAthletes.map((athlete) => (
                <option key={athlete.number} value={athlete.number}>
                  {athlete.number === "all"
                    ? "Todos os atletas"
                    : `#${athlete.number} - ${athlete.name}`}
                </option>
              ))}
            </select>
          </div>

          {/* Seletor de zona */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zona
            </label>
            <select
              value={zoneType}
              onChange={(e) => setZoneType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="start">Origem (Zona Inicial)</option>
              <option value="end">Destino (Zona Final)</option>
            </select>
          </div>

          {/* Seletor de fundamento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fundamento
            </label>
            <select
              value={selectedFundamento}
              onChange={(e) => setSelectedFundamento(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(fundamentosMap).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          {/* Seletor de qualidade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Qualidade
            </label>
            <select
              value={selectedQuality}
              onChange={(e) => setSelectedQuality(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(qualidadesMap).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Grid de quadras */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
        {[1, 2, 3, 4, 5, 6].map((rotationNumber) =>
          renderCourt(rotationNumber)
        )}
      </div>

      {/* Mensagem quando não há dados */}
      {Object.keys(rotationData[selectedTeam]).length === 0 && (
        <div className="mt-8 text-center text-gray-500">
          <p>Não há dados para os filtros selecionados</p>
          <p className="text-sm mt-2">
            {fundamentosMap[selectedFundamento]} -{" "}
            {qualidadesMap[selectedQuality]} -
            {zoneType === "start" ? " Origem" : " Destino"} -
            {selectedAthlete === "all"
              ? " Todos os atletas"
              : ` Atleta #${selectedAthlete}`}
          </p>
        </div>
      )}

      {/* Legenda */}
      <div className="mt-6 text-center text-sm text-gray-600">
        <p>
          Legenda: Números representam a quantidade de{" "}
          {fundamentosMap[selectedFundamento].toLowerCase()}s por zona
        </p>
        <p>Zonas: 1-9 conforme posicionamento na quadra</p>
        <p className="mt-2 text-xs">
          {Object.keys(rotationData[selectedTeam]).join(", ") ||
            "Nenhuma rotação encontrada"}
        </p>
      </div>
    </div>
  );
};

export default RotationReport;
